import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Play, ExternalLink, X, Eye, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";

// 12 Curated Masterpieces (3 Websites, 3 Branding Logos, 3 Videos, 3 Social Media)
const allWorks = [
  // --- WEBSITES ---
  {
    id: "web-1",
    category: "web",
    tag: "WEB ENGINEERING",
    name: "Cropflyt Technologies",
    niche: "Aviation Tech Portal",
    image: "/works/websites/croptflyt.png",
    url: "https://cropflyt-final.vercel.app/en",
    serviceLink: "/services/web-development",
    metric: "0.2s Load Time",
    desc: "Agricultural aviation portal leveraging headless CMS and interactive maps."
  },
  {
    id: "web-2",
    category: "web",
    tag: "WEB ENGINEERING",
    name: "Lewana Hotel Dubai",
    niche: "Luxury Hospitality Platform",
    image: "/works/websites/lewanahoteldubai.png",
    url: "https://www.lewanahoteldubai.com/",
    serviceLink: "/services/web-development",
    metric: "+180% Bookings",
    desc: "A custom hotel booking engine built with Next.js for high-speed conversions."
  },
  {
    id: "web-3",
    category: "web",
    tag: "WEB ENGINEERING",
    name: "Claura",
    niche: "Sleek E-Commerce Storefront",
    image: "/works/websites/claura.png",
    url: "https://claura.in/",
    serviceLink: "/services/web-development",
    metric: "2.1x Conversion",
    desc: "Mobile-first lifestyle storefront built with React and Tailwind CSS."
  },

  // --- BRANDING ---
  {
    id: "brand-1",
    category: "branding",
    tag: "BRANDING & IDENTITY",
    name: "VOW Aesthetics Wellness",
    niche: "Wedding Editorial & Film",
    image: "/images/branding/vowes.jpg",
    serviceLink: "/services/branding",
    metric: "Elegant Curation",
    desc: "Premium visual identity, logo curation, and editorial layout systems."
  },
  {
    id: "brand-2",
    category: "branding",
    tag: "BRANDING & IDENTITY",
    name: "Write on Walls",
    niche: "Creative Design Agency",
    image: "/images/branding/writeonwalls.jpg",
    serviceLink: "/services/branding",
    metric: "Avant-Garde Typography",
    desc: "Bespoke modern logo design and architectural graphics system."
  },
  {
    id: "brand-3",
    category: "branding",
    tag: "BRANDING & IDENTITY",
    name: "Skin City",
    niche: "Premium Dermatology",
    image: "/images/branding/skincity.jpg",
    serviceLink: "/services/branding",
    metric: "Minimalist Health",
    desc: "Sleek, clinical branding and medical identity design."
  },
  {
    id: "brand-4",
    category: "branding",
    tag: "BRANDING & IDENTITY",
    name: "Sajanvat Jwellers",
    niche: "Luxury Festive Decor",
    image: "/images/branding/sajanvat.jpg",
    serviceLink: "/services/branding",
    metric: "Ethnic Splendor",
    desc: "Heritage branding and catalog designs celebrating festive aesthetics."
  },
  {
    id: "brand-5",
    category: "branding",
    tag: "BRANDING & IDENTITY",
    name: "Abode",
    niche: "Bespoke Furnishings",
    image: "/images/branding/abode.jpg",
    serviceLink: "/services/branding",
    metric: "Modern Luxury",
    desc: "Refined architectural logo design and high-end brand collateral."
  },

  // --- VIDEOS ---
  {
    id: "video-1",
    category: "video",
    tag: "CINEMATIC MEDIA",
    name: "1930 — Cybercrime Awareness Short Film",
    niche: "Psychological Drama Short",
    image: "/images/films/1930-awards-poster.jpg",
    youtubeEmbedUrl: "https://www.youtube.com/embed/KAnKujVDjf4?autoplay=1&rel=0",
    serviceLink: "/services/film-making",
    metric: "4x Winner & 5 Fest Selections",
    desc: "An award-winning narrative film exploring digital arrest and fraud prevention."
  },
  {
    id: "video-twowombs",
    category: "video",
    tag: "CINEMATIC MEDIA",
    name: "Two Wombs, One Story",
    niche: "Narrative Short Film",
    image: "/images/films/two-wombs-one-story-poster.jpg",
    serviceLink: "/services/film-making",
    metric: "Releasing 24th July",
    desc: "Vanitaa Vins presents A Baaton Mein Productions — Directed by Akshay Yagnik, Story by Chitra Katyal."
  },
  {
    id: "video-2",
    category: "video",
    tag: "CINEMATIC MEDIA",
    name: "Aurora — Short Film",
    niche: "Mystery Noir Thriller",
    image: "https://images.unsplash.com/photo-1611540881474-bf7c8731bfd2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwzfHxjaW5lbWF0aWMlMjBmaWxtJTIwcHJvZHVjdGlvbnxlbnwwfHx8fDE3Nzc3MTA5MTR8MA&ixlib=rb-4.1.0&q=85",
    youtubeEmbedUrl: "https://www.youtube.com/embed/KAnKujVDjf4?autoplay=1&rel=0",
    serviceLink: "/services/film-making",
    metric: "3 Festivals",
    desc: "A gripping drama shot with practical lighting and cinematic lenses."
  },
  {
    id: "video-3",
    category: "video",
    tag: "CINEMATIC MEDIA",
    name: "Amber — Commercial Spot",
    niche: "Kinetic Brand Commercial",
    image: "https://images.pexels.com/photos/8088372/pexels-photo-8088372.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    youtubeEmbedUrl: "https://www.youtube.com/embed/KAnKujVDjf4?autoplay=1&rel=0",
    serviceLink: "/services/film-making",
    metric: "Clio Nominee",
    desc: "A stunning 60-second ad spot directed with intense visual harmony."
  },

  // --- SOCIAL MEDIA ---
  {
    id: "social-1",
    category: "social",
    tag: "SOCIAL CAMPAIGN",
    name: "Sajwat Festive Feed",
    niche: "Premium Organic Aesthetics",
    image: "/works/Social Media Posts/sajwat-post.png",
    serviceLink: "/services/social-media",
    metric: "+150% Reach",
    desc: "Ethnic styling and curation for organic home decor campaigns."
  },
  {
    id: "social-2",
    category: "social",
    tag: "SOCIAL CAMPAIGN",
    name: "Saanvi Festive Campaign",
    niche: "Ethnic Fashion Feed",
    image: "/works/Social Media Posts/saanvi-ethnicos-post1.png",
    serviceLink: "/services/social-media",
    metric: "32% CTR Hike",
    desc: "Auto-scrolling catalog post showcasing beautiful festive wear."
  },
  {
    id: "social-3",
    category: "social",
    tag: "SOCIAL CAMPAIGN",
    name: "Flare Modern Retail",
    niche: "High-Engagement Feeds",
    image: "/works/Social Media Posts/flarebyblaze-post1.png",
    serviceLink: "/services/social-media",
    metric: "4.9k Shares",
    desc: "Eye-catching graphic design for retail social media campaigns."
  }
];

const OurWorksShowcase = () => {
  const [lightboxVideo, setLightboxVideo] = useState(null);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const scrollRef = useRef(null);
  const timeoutRef = useRef(null);

  // Double the works array to ensure perfect seamless looping
  const doubledWorks = [...allWorks, ...allWorks];

  useEffect(() => {
    if (!isAutoPlaying) return;

    let animationFrameId;
    const scrollContainer = scrollRef.current;

    const scroll = () => {
      if (scrollContainer) {
        scrollContainer.scrollLeft += 4.2; // smooth fast glide speed

        const halfWidth = scrollContainer.scrollWidth / 2;
        if (scrollContainer.scrollLeft >= halfWidth) {
          scrollContainer.scrollLeft = 0;
        }
      }
      animationFrameId = requestAnimationFrame(scroll);
    };

    animationFrameId = requestAnimationFrame(scroll);
    return () => cancelAnimationFrame(animationFrameId);
  }, [isAutoPlaying]);

  const handleManualScroll = (direction) => {
    // Stop autoplay
    setIsAutoPlaying(false);

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    if (scrollRef.current) {
      const scrollAmount = direction === "left" ? -430 : 430; // card width + gap
      scrollRef.current.scrollBy({ left: scrollAmount, behavior: "smooth" });
    }

    // Resume autoplay after 4 seconds of inactivity
    timeoutRef.current = setTimeout(() => {
      setIsAutoPlaying(true);
    }, 4000);
  };

  return (
    <section id="showcase" className="relative py-32 overflow-hidden bg-[var(--ink)] border-t border-[var(--rule)]">
      {/* Dynamic Background Light Effect */}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[900px] h-[600px] bg-[radial-gradient(circle_at_center,rgba(201,169,97,0.038)_0%,transparent_75%)] pointer-events-none" />

      <div className="w-full relative z-10">

        {/* Section Header */}
        <div className="container-xl px-6 mb-20 text-center max-w-4xl mx-auto">
          <div className="flex items-center justify-center gap-4 mb-4">
            <span className="font-impact text-[11px] tracking-[0.4em] text-[var(--gold)]">06</span>
            <span className="w-16 h-px bg-[var(--rule)]" />
            <span className="font-impact text-[11px] tracking-[0.4em] text-[var(--cream-muted)] uppercase">OUR WORKS</span>
          </div>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9 }}
            className="mt-6 font-display text-[clamp(42px,6vw,84px)] leading-[1.05] text-[var(--cream)] balance"
          >
            Masterpieces crafted with <span className="italic gold-text">meraki</span>.
          </motion.h2>
          <p className="mt-6 text-[var(--cream-muted)] text-base md:text-lg font-body leading-relaxed max-w-2xl mx-auto">
            An endless auto-scrolling gallery showcasing our premium deployments. Click left or right buttons to navigate manually.
          </p>
        </div>

        {/* Manual navigation controls row aligned top-left of the slider */}
        <div className="container-xl px-6 mb-8 flex justify-between items-center max-w-7xl mx-auto">
          <div className="flex gap-4">
            <button
              onClick={() => handleManualScroll("left")}
              className="w-12 h-12 rounded-full border border-white/10 hover:border-[var(--gold)] bg-black/40 hover:bg-[var(--gold)] text-white hover:text-black flex items-center justify-center transition-all duration-300 shadow-md backdrop-blur-md focus:outline-none"
              aria-label="Slide Left"
            >
              <span className="text-lg">←</span>
            </button>
            <button
              onClick={() => handleManualScroll("right")}
              className="w-12 h-12 rounded-full border border-white/10 hover:border-[var(--gold)] bg-black/40 hover:bg-[var(--gold)] text-white hover:text-black flex items-center justify-center transition-all duration-300 shadow-md backdrop-blur-md focus:outline-none"
              aria-label="Slide Right"
            >
              <span className="text-lg">→</span>
            </button>
          </div>
          <div className="hidden sm:block text-[10px] uppercase tracking-[0.3em] text-[var(--gold)] font-impact">
            Interactive Ribbon
          </div>
        </div>

        {/* INFINITE AUTO-SLIDING CUSTOM SECTION */}
        <div className="w-full relative overflow-hidden py-8 border-y border-[var(--rule)] bg-black/10">
          <div
            ref={scrollRef}
            className="w-full flex gap-6 overflow-x-auto scrollbar-none py-4 select-none"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            onMouseEnter={() => setIsAutoPlaying(false)}
            onMouseLeave={() => setIsAutoPlaying(true)}
          >
            {doubledWorks.map((work, idx) => (
              <div
                key={`${work.id}-${idx}`}
                className="w-[320px] sm:w-[410px] h-[500px] sm:h-[570px] bg-black/45 rounded-3xl overflow-hidden border border-white/5 shadow-2xl hover:border-[var(--gold)]/40 hover:shadow-[0_15px_40px_rgba(0,0,0,0.85)] transition-all duration-500 flex flex-col group mx-3 select-none shrink-0"
              >

                {/* Visual Viewport Wrapper */}
                <div className="relative h-[250px] sm:h-[300px] w-full overflow-hidden bg-[#0d0c0a] border-b border-white/5">

                  {/* --- WEBSITE CARD VIEWPORT --- */}
                  {work.category === "web" && (
                    <div className="w-full h-full flex flex-col justify-start relative">
                      {/* macOS address mock */}
                      <div className="flex items-center gap-1.5 px-4 py-2 bg-[#0f0e0d] border-b border-white/5 absolute top-0 left-0 w-full z-20">
                        <span className="w-2 h-2 rounded-full bg-[#ff5f56]" />
                        <span className="w-2 h-2 rounded-full bg-[#ffbd2e]" />
                        <span className="w-2 h-2 rounded-full bg-[#27c93f]" />
                        <span className="mx-auto text-[8px] text-[var(--cream-muted)] font-mono truncate max-w-[150px] pl-4">
                          {work.url.replace("https://", "").replace("www.", "")}
                        </span>
                      </div>

                      <div className="w-full h-full pt-8 overflow-hidden relative">
                        <img
                          src={work.image}
                          alt={work.name}
                          className="w-full website-screenshot-scroll transition-transform duration-[7.5s] ease-[cubic-bezier(0.215,0.61,0.355,1)]"
                          style={{ objectPosition: "top", objectFit: "cover" }}
                        />

                        {/* Scroll overlay */}
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none flex items-center justify-center backdrop-blur-[1px]">
                          <div className="px-4 py-1.5 bg-black/75 border border-white/10 backdrop-blur-md rounded-full text-[8px] font-impact text-[var(--gold)] uppercase tracking-wider">
                            Hover to Scroll
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* --- BRANDING CARD VIEWPORT --- */}
                  {work.category === "branding" && (
                    <div className="w-full h-full flex items-center justify-center p-12 relative bg-[radial-gradient(circle_at_center,#1d1915_0%,#0d0c0a_100%)]">
                      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(201,169,97,0.06)_0%,transparent_75%)] opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none" />
                      <img
                        src={work.image}
                        alt={work.name}
                        className="max-h-[100px] max-w-full object-contain grayscale opacity-65 group-hover:grayscale-0 group-hover:opacity-100 transition-all duration-700 transform group-hover:scale-110 drop-shadow-[0_0_12px_rgba(255,255,255,0.05)]"
                      />
                    </div>
                  )}

                  {/* --- VIDEOS CARD VIEWPORT --- */}
                  {work.category === "video" && (
                    <div className="w-full h-full relative">
                      <div
                        className="absolute inset-0 bg-cover bg-center grayscale opacity-55 group-hover:grayscale-0 group-hover:opacity-75 group-hover:scale-105 transition-all duration-700"
                        style={{ backgroundImage: `url("${work.image}")` }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[var(--ink)]/80 via-transparent to-transparent pointer-events-none" />

                      {/* Play overlay button */}
                      <div
                        onClick={() => setLightboxVideo(work.youtubeEmbedUrl)}
                        className="absolute inset-0 flex items-center justify-center cursor-pointer z-20 bg-black/20 group-hover:bg-black/0 transition-colors"
                      >
                        <button className="w-14 h-14 rounded-full bg-[var(--ink)]/75 border border-white/10 hover:border-[var(--gold)] group-hover:bg-[var(--gold)] group-hover:text-black flex items-center justify-center shadow-2xl backdrop-blur-md transform transition-all duration-500 scale-100 group-hover:scale-110">
                          <Play size={16} className="ml-1 fill-current" />
                        </button>
                      </div>
                    </div>
                  )}

                  {/* --- SOCIAL MEDIA CARD VIEWPORT --- */}
                  {work.category === "social" && (
                    <div className="w-full h-full relative overflow-hidden">
                      <div
                        className="absolute inset-0 bg-cover bg-center grayscale opacity-55 group-hover:grayscale-0 group-hover:opacity-85 group-hover:scale-105 transition-all duration-700"
                        style={{ backgroundImage: `url("${work.image}")` }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[var(--ink)]/80 via-transparent to-transparent pointer-events-none" />

                      {/* Zoom overlay button */}
                      <div
                        onClick={() => setLightboxImage(work.image)}
                        className="absolute inset-0 flex items-center justify-center cursor-pointer z-20 bg-black/25 group-hover:bg-black/0 transition-colors"
                      >
                        <button className="w-14 h-14 rounded-full bg-[var(--ink)]/75 border border-white/10 hover:border-[var(--gold)] group-hover:bg-[var(--gold)] group-hover:text-black flex items-center justify-center shadow-2xl backdrop-blur-md transform transition-all duration-500 scale-100 group-hover:scale-110">
                          <Eye size={16} />
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Category overlay tags */}
                  <div className="absolute top-4 right-4 z-20">
                    <span className="font-impact text-[7px] sm:text-[8px] tracking-[0.2em] px-2.5 py-1 rounded-full border border-white/10 bg-black/60 text-white backdrop-blur-md">
                      {work.category.toUpperCase()}
                    </span>
                  </div>

                </div>

                {/* Narrative Details Block */}
                <div className="p-6 sm:p-8 flex flex-col justify-between flex-1 relative bg-black/20">
                  <div>
                    <div className="flex items-center justify-between gap-4 mb-3">
                      <span className="font-impact text-[8px] tracking-[0.25em] bg-white/5 border border-white/10 text-[var(--gold)] px-2.5 py-0.5 rounded-md uppercase">
                        {work.tag}
                      </span>
                      <span className="font-impact text-[9px] sm:text-[10px] tracking-[0.1em] text-[var(--gold)]">
                        {work.metric}
                      </span>
                    </div>
                    <h3 className="font-display text-xl sm:text-2xl text-[var(--cream)] group-hover:text-[var(--gold-bright)] transition-colors duration-300">
                      {work.name}
                    </h3>
                    <p className="mt-3 text-[12px] sm:text-[13px] text-[var(--cream-muted)] leading-relaxed font-body line-clamp-3">
                      {work.desc}
                    </p>
                  </div>

                  {/* Card bottom action controls */}
                  <div className="mt-6 pt-4 border-t border-white/[0.03] flex items-center justify-between">
                    {work.category === "web" ? (
                      <a
                        href={work.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1.5 text-[var(--cream-muted)] hover:text-[var(--gold)] transition-colors text-[9px] font-impact tracking-widest uppercase"
                      >
                        <ExternalLink size={11} /> Launch Site
                      </a>
                    ) : work.category === "video" ? (
                      <button
                        onClick={() => setLightboxVideo(work.youtubeEmbedUrl)}
                        className="flex items-center gap-1.5 text-[var(--cream-muted)] hover:text-[var(--gold)] transition-colors text-[9px] font-impact tracking-widest uppercase focus:outline-none"
                      >
                        <Play size={10} /> Watch Film
                      </button>
                    ) : work.category === "social" ? (
                      <button
                        onClick={() => setLightboxImage(work.image)}
                        className="flex items-center gap-1.5 text-[var(--cream-muted)] hover:text-[var(--gold)] transition-colors text-[9px] font-impact tracking-widest uppercase focus:outline-none"
                      >
                        <Eye size={10} /> View Post
                      </button>
                    ) : (
                      <div />
                    )}

                    <Link
                      to={work.serviceLink}
                      className="flex items-center gap-1 text-[var(--gold)] hover:text-[var(--gold-bright)] transition-colors text-[9px] font-impact tracking-widest uppercase"
                    >
                      Service Detail <ArrowUpRight size={12} />
                    </Link>
                  </div>

                </div>

              </div>
            ))}
          </div>
        </div>

        {/* Global callout guidance info */}
        <div className="mt-16 text-center px-6">
          <p className="text-[11px] text-[var(--gold)]/80 italic font-body flex items-center justify-center gap-2">
            <Sparkles size={14} className="text-[var(--gold)]" /> Use the navigation arrows above to manually explore our premium studio portfolio.
          </p>
        </div>

      </div>

      {/* 1. PREMIUM VIDEO LIGHTBOX MODAL */}
      <AnimatePresence>
        {lightboxVideo && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[999] flex items-center justify-center p-4 bg-black/90 backdrop-blur-xl"
            onClick={() => setLightboxVideo(null)}
          >
            <motion.div
              initial={{ scale: 0.9, y: 30 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 30 }}
              transition={{ type: "spring", damping: 25, stiffness: 180 }}
              className="relative w-full max-w-4xl aspect-video rounded-3xl overflow-hidden border border-[rgba(201,169,97,0.25)] shadow-[0_30px_60px_rgba(0,0,0,0.85)] bg-black"
              onClick={(e) => e.stopPropagation()}
            >
              <iframe
                className="w-full h-full border-0 absolute inset-0"
                src={lightboxVideo}
                title="YouTube video player"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
              />

              <button
                onClick={() => setLightboxVideo(null)}
                className="absolute top-4 right-4 z-[1000] p-2.5 bg-black/85 hover:bg-[var(--gold)] hover:text-black border border-white/10 rounded-full text-white transition-all shadow-lg backdrop-blur-md flex items-center justify-center focus:outline-none"
              >
                <X size={18} />
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 2. PREMIUM SOCIAL MEDIA IMAGE LIGHTBOX MODAL */}
      <AnimatePresence>
        {lightboxImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[999] flex items-center justify-center p-4 bg-black/90 backdrop-blur-xl"
            onClick={() => setLightboxImage(null)}
          >
            <motion.div
              initial={{ scale: 0.9, y: 30 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 30 }}
              transition={{ type: "spring", damping: 25, stiffness: 180 }}
              className="relative w-full max-w-[500px] aspect-[4/5] rounded-3xl overflow-hidden border border-[rgba(201,169,97,0.25)] shadow-[0_30px_60px_rgba(0,0,0,0.85)] bg-black flex items-center justify-center p-2"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={lightboxImage}
                alt="Social Media Work"
                className="w-full h-full object-cover rounded-[20px]"
              />

              <button
                onClick={() => setLightboxImage(null)}
                className="absolute top-4 right-4 z-[1000] p-2.5 bg-black/85 hover:bg-[var(--gold)] hover:text-black border border-white/10 rounded-full text-white transition-all shadow-lg backdrop-blur-md flex items-center justify-center focus:outline-none"
              >
                <X size={18} />
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

    </section>
  );
};

export default OurWorksShowcase;
